import { Injectable } from '@angular/core';
import {
  MixedSchema,
  initMarkdownSchema,
  mergeSchemas,
} from '@principle-theorem/editor';
import { TypedFormControl } from '@principle-theorem/ng-shared';
import {
  IClinicalNote,
  IPatient,
  IScribeNote,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  WithRef,
  snapshot,
} from '@principle-theorem/shared';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { IScribeInsertTarget } from '../tokens/ai-scribe-insert-target';

/**
 * The clinical-note editor surface a completed scribe note can be inserted into.
 * The focused `ClinicalNotesComponent` registers this live so the floating
 * recorder and the sessions panel always target the note the clinician is
 * actually editing, along with its live lock state.
 */
export interface IScribeInsertRegistration {
  patientRef: DocumentReference<IPatient>;
  noteRef: DocumentReference<IClinicalNote>;
  noteControl: TypedFormControl<MixedSchema>;
  immutable$: Observable<boolean>;
}

/**
 * The implementation of the `AI_SCRIBE_INSERT_TARGET` seam the host app binds.
 * It bridges the floating recorder to whichever editor surface is focused on a
 * patient (today the clinical note editor): the editor registers its
 * `FormControl` and lock state here, and both the recorder and the Scribe
 * Sessions panel insert through it. It lives here rather than in the charting
 * library because it has no charting dependency, so any other editor surface
 * (e.g. documents) can register with it too.
 *
 * Insertion always flows through the editor's `FormControl` (mirroring
 * `addHeidiNote`) so the existing auto-save pipeline persists the note; the
 * clinical note document content is never patched directly.
 */
@Injectable({ providedIn: 'root' })
export class ScribeNoteInserterService implements IScribeInsertTarget {
  private readonly _target$ = new BehaviorSubject<
    IScribeInsertRegistration | undefined
  >(undefined);

  /**
   * The patient the focused editor can accept an insert for, or `undefined` when
   * no editor is focused. Only the patient ref is exposed — this is what the
   * recorder subscribes to via the `AI_SCRIBE_INSERT_TARGET` token.
   */
  readonly activeTarget$: Observable<
    { patientRef: DocumentReference<IPatient> } | undefined
  > = this._target$.pipe(
    map((target) => target?.patientRef),
    distinctUntilChanged((before, after) => before?.path === after?.path),
    map((patientRef) => (patientRef ? { patientRef } : undefined))
  );

  /**
   * Whether the registered note is currently locked. Consumers disable their
   * Insert affordance while this is `true`; `false` when nothing is registered.
   */
  readonly targetLocked$: Observable<boolean> = this._target$.pipe(
    switchMap((target) => (target ? target.immutable$ : of(false))),
    distinctUntilChanged()
  );

  /**
   * The full active insert registration, or `undefined` when no editor is
   * focused. Exposes the live editor `FormControl` and note ref so the Scribe
   * Review dialog can render today's clinical note as context in its left pane.
   * Deliberately NOT part of the `IScribeInsertTarget` token surface — unlike
   * `activeTarget$` (patient-ref only), this is for callers that own the editor.
   */
  readonly activeRegistration$: Observable<
    IScribeInsertRegistration | undefined
  > = this._target$.asObservable();

  /** Point insertion at the clinical note the given editor surface is editing. */
  registerTarget(target: IScribeInsertRegistration): void {
    this._target$.next(target);
  }

  /**
   * Forget the registered surface (e.g. when the note editor is destroyed).
   * Pass the registration this caller registered to only clear when it is still
   * the active target — so one of two concurrently-mounted editors tearing down
   * cannot wipe the other's registration under last-writer-wins.
   */
  clearTarget(registration?: IScribeInsertRegistration): void {
    if (registration && this._target$.value !== registration) {
      return;
    }
    this._target$.next(undefined);
  }

  /**
   * Insert a completed scribe note into the registered clinical note. Rejects
   * when no editor is focused or the target note is locked. The markdown is
   * parsed and merged into the editor's `FormControl` (never patched onto the
   * clinical note document); the destination is recorded on the scribe note via
   * `insertedInto`.
   */
  async insert(note: WithRef<IScribeNote>): Promise<void> {
    const target = this._target$.value;
    if (!target) {
      throw new Error(
        'Cannot insert scribe note: no clinical note is focused.'
      );
    }
    const locked = await snapshot(target.immutable$);
    if (locked) {
      throw new Error(
        'Cannot insert scribe note: the clinical note is locked.'
      );
    }
    const updatedContent = mergeSchemas([
      target.noteControl.value,
      initMarkdownSchema(note.content),
    ]);
    target.noteControl.setValue(updatedContent);
    await Firestore.patchDoc(note.ref, {
      insertedInto: target.noteRef,
    });
  }
}
